import {
  ArrowUUpLeftIcon,
  DownloadSimpleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PauseIcon,
  PencilIcon,
  PlusIcon,
  ProhibitIcon,
} from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useOutletContext } from 'react-router-dom'

import { useCheckInGuest } from '@/api/visitors/check-in-guest'
import { useExportGuests } from '@/api/visitors/export-guests'
import { GET_GUESTS_BASE_QUERY_KEY, useGetGuests } from '@/api/visitors/get-guests'
import {
  GetVisitorCountsByClientRequest,
  getVisitorCountsByClient,
} from '@/api/visitors/get-visitor-counts-by-client'
import { useUpdateVisitorStatus } from '@/api/visitors/update-visitor-status'
import { usePersonColumnSettings } from '@/components/business/person-column'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Icon } from '@/components/ui/icon'
import { Select, useSelectOptions } from '@/components/ui/select'
import { StatusTabs } from '@/components/ui/status-tabs'
import { RowAction, SortOrder } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { Typography } from '@/components/ui/typography'
import LegacyGuests from '@/components/Visitors/Guests/Guests'
import { popupFactory, toastFactory } from '@/factories'
import { useFilters, useSearchParams } from '@/hooks'
import { GuestModel, LockedResident, VisitorStatus } from '@/models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { Building } from '@/reducers/types'
import { buildCsvFilename, getApiErrorMessage } from '@/utils'

import { AddVisitorModal } from '../add-visitor-modal'
import { BanVisitorModal } from '../ban-visitor-modal'
import { GuestDetailsModal } from '../guest-details-modal'
import { VisitorsDashboardOutletContext } from '../visitors-dashboard.page'
import { getVisitorStatusTabs, useDefaultVisitorsSort } from '../visitors-dashboard.utils'
import {
  ADDITIONAL_FILTERS_DROPDOWN_WIDTH,
  getGuestColumns,
  visitorsGuestsFiltersSchema,
  visitorsGuestsInitialFilters,
} from './visitors-guests.constants'

export type VisitorsGuestsPageProps = {
  resident?: LockedResident
  building?: Building | null
}

export const VisitorsGuestsPage = ({ resident, building }: VisitorsGuestsPageProps = {}) => {
  const { t } = useTranslation(['visitors'])
  const globalBuilding = useSelector(selectActiveBuilding)
  const activeBuilding = building ?? globalBuilding
  const buildingId = activeBuilding?.id
  const timezone = activeBuilding?.timezone ?? 'America/Chicago'
  const residentId = resident?.id

  // Note: this page is also rendered directly (not through VisitorsDashboardPage's Outlet) when
  // embedded in the Resident Profile, so this context may be absent - fall back to opening the
  // edit modal locally and always showing the new design in that case.
  const outletContext = useOutletContext<VisitorsDashboardOutletContext | undefined>()
  const uiVersion = outletContext?.uiVersion ?? 'new'
  const onEditGuest =
    outletContext?.onEditGuest ??
    ((guest: GuestModel) => popupFactory.open(AddVisitorModal, { editingVisitor: guest }))

  const searchParams = useSearchParams({
    initialState: visitorsGuestsInitialFilters,
    schema: visitorsGuestsFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: visitorsGuestsInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  useDefaultVisitorsSort({ filters, searchParams })

  const status = filters.getFilter('status')
  const search = filters.getFilter('search')
  const clientId = residentId ? [residentId] : filters.getFilter('clientId')
  const statusFilter = status === 'all' ? undefined : status
  const sortBy = filters.getFilter('sortBy')
  const sortOrder = filters.getFilter('sortOrder')

  const {
    data,
    isFetching: isGuestsFetching,
    error: guestsError,
  } = useGetGuests({
    buildingId,
    clientId,
    direction: sortOrder,
    filter: statusFilter,
    limit: filters.getFilter('limit'),
    search: search || undefined,
    skip: filters.getFilter('skip'),
    sort: sortBy,
  })

  const { data: guests = [], total = 0, countByStatus } = data ?? {}

  const residentsOptions = useSelectOptions(
    {
      query: (params: GetVisitorCountsByClientRequest, signal?: AbortSignal) =>
        getVisitorCountsByClient({ ...params, search: params.search || search }, signal),
      requestData: { buildingId, filter: statusFilter, type: 'guest' },
      transform: (residentsData) => {
        const residents = residentsData?.data ?? []
        return residents.map((client) => ({
          label: `${client.name} - (${client.count})`,
          value: client.clientId,
        }))
      },
    },
    {
      enabled: !!buildingId,
      queryKey: ['getVisitorCountsByClient', 'guest', buildingId, search, statusFilter],
    },
  )

  const { mutateAsync: checkInGuest } = useCheckInGuest()
  const { mutateAsync: updateStatus } = useUpdateVisitorStatus()
  const { mutateAsync: downloadGuests, isPending: isExporting } = useExportGuests()

  const handleExportCSV = () => {
    if (!buildingId) return
    void downloadGuests({
      buildingId,
      clientId,
      direction: sortOrder,
      fileName: buildCsvFilename({ building: activeBuilding?.name, page: 'Guests' }),
      filter: statusFilter,
      search: search || undefined,
      sort: sortBy,
    })
  }

  const typeLabel = t('visitors:snackbar.typeGuest')

  const handleCheckIn = (guest: GuestModel) => {
    popupFactory.open(ConfirmationPopup, {
      children: t('visitors:dashboard.confirm.checkInBody', { name: guest.name }),
      confirmText: t('visitors:columns.checkIn'),
      onConfirm: async () => {
        try {
          await checkInGuest({ clientId: guest.clientId, guestId: guest.id })
          toastFactory.success(t('visitors:dashboard.snackbar.checkedIn', { name: guest.name }))
        } catch (error) {
          toastFactory.error(
            getApiErrorMessage(error) ??
              t('visitors:dashboard.snackbar.errorCheckingIn', { type: typeLabel }),
          )
          throw error
        }
      },
      title: t('visitors:dashboard.confirm.checkInTitle', { type: typeLabel }),
      variant: 'info',
    })
  }

  const handleBan = (guest: GuestModel) => {
    popupFactory.open(BanVisitorModal, {
      name: guest.name,
      onConfirm: async (reason: string) => {
        try {
          await updateStatus({
            banned: true,
            bannedReason: reason,
            id: guest.id,
            type: 'guest',
          })
          toastFactory.success(t('visitors:dashboard.snackbar.banned', { name: guest.name }))
        } catch (error) {
          toastFactory.error(
            getApiErrorMessage(error) ??
              t('visitors:dashboard.snackbar.errorBanning', { type: typeLabel }),
          )
          throw error
        }
      },
      type: 'guest',
    })
  }

  const handleUnban = (guest: GuestModel) => {
    popupFactory.open(ConfirmationPopup, {
      children: t('visitors:dashboard.confirm.unbanBody', { name: guest.name }),
      confirmText: t('visitors:columns.unban'),
      onConfirm: async () => {
        try {
          await updateStatus({
            banned: false,
            bannedReason: '',
            id: guest.id,
            type: 'guest',
          })
          toastFactory.success(t('visitors:dashboard.snackbar.unbanned', { name: guest.name }))
        } catch (error) {
          toastFactory.error(
            getApiErrorMessage(error) ??
              t('visitors:dashboard.snackbar.errorUnbanning', { type: typeLabel }),
          )
          throw error
        }
      },
      title: t('visitors:dashboard.confirm.unbanTitle', { type: typeLabel }),
      variant: 'warning',
    })
  }

  const handleSuspend = (guest: GuestModel) => {
    popupFactory.open(ConfirmationPopup, {
      children: t('visitors:dashboard.confirm.suspendBody', { name: guest.name }),
      confirmText: t('visitors:columns.suspend'),
      onConfirm: async () => {
        try {
          await updateStatus({
            id: guest.id,
            suspended: true,
            type: 'guest',
          })
          toastFactory.success(t('visitors:dashboard.snackbar.suspended', { name: guest.name }))
        } catch (error) {
          toastFactory.error(
            getApiErrorMessage(error) ??
              t('visitors:dashboard.snackbar.errorSuspending', { type: typeLabel }),
          )
          throw error
        }
      },
      title: t('visitors:dashboard.confirm.suspendTitle', { type: typeLabel }),
      variant: 'warning',
    })
  }

  const handleUnsuspend = (guest: GuestModel) => {
    popupFactory.open(ConfirmationPopup, {
      children: t('visitors:dashboard.confirm.unsuspendBody', { name: guest.name }),
      confirmText: t('visitors:columns.unsuspend'),
      onConfirm: async () => {
        try {
          await updateStatus({
            id: guest.id,
            suspended: false,
            type: 'guest',
          })
          toastFactory.success(t('visitors:dashboard.snackbar.unsuspended', { name: guest.name }))
        } catch (error) {
          toastFactory.error(
            getApiErrorMessage(error) ??
              t('visitors:dashboard.snackbar.errorUnsuspending', { type: typeLabel }),
          )
          throw error
        }
      },
      title: t('visitors:dashboard.confirm.unsuspendTitle', { type: typeLabel }),
      variant: 'info',
    })
  }

  const handleEdit = (guest: GuestModel) => {
    onEditGuest(guest)
  }

  const residentColumnSettings = usePersonColumnSettings({
    column: 'resident',
    dashboard: 'Visitors',
    fieldType: 'resident',
    rowsQueryKey: [GET_GUESTS_BASE_QUERY_KEY],
  })

  const columns = getGuestColumns({
    customAccessName: activeBuilding?.customAccessName,
    onCheckIn: handleCheckIn,
    residentColumnSettings,
    t,
    timezone,
  })

  const rowActions: RowAction<GuestModel>[] = [
    {
      icon: EyeIcon,
      label: t('visitors:columns.details'),
      onClick: (guest) =>
        popupFactory.open(GuestDetailsModal, { clientId: guest.clientId, guestId: guest.id }),
      primary: true,
    },
    {
      hidden: (guest) => guest.banned,
      icon: ProhibitIcon,
      label: t('visitors:columns.ban'),
      onClick: handleBan,
      variant: 'danger',
    },
    {
      hidden: (guest) => !guest.banned,
      icon: ArrowUUpLeftIcon,
      label: t('visitors:columns.unban'),
      onClick: handleUnban,
    },
    {
      hidden: (guest) => guest.suspended,
      icon: PauseIcon,
      label: t('visitors:columns.suspend'),
      onClick: handleSuspend,
    },
    {
      hidden: (guest) => !guest.suspended,
      icon: ArrowUUpLeftIcon,
      label: t('visitors:columns.unsuspend'),
      onClick: handleUnsuspend,
    },
    {
      hidden: (guest) => guest.banned || guest.suspended,
      icon: PencilIcon,
      label: t('visitors:columns.edit'),
      onClick: handleEdit,
    },
  ]

  const statusTabOptions = getVisitorStatusTabs(t, countByStatus)

  const handleStatusChange = (newStatus: VisitorStatus | 'all') => {
    filters.setFilter('status', newStatus)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    filters.setFilter('search', e.target.value)
  }

  const handleResidentChange = (clientIds: number[]) => {
    filters.setFilter('clientId', clientIds)
  }

  const handleAddGuest = () => {
    popupFactory.open(AddVisitorModal, {
      defaultVisitorType: 'guest',
      lockBuilding: building,
      lockResident: resident,
    })
  }

  const handleSort = (newSortBy: string | null, newSortOrder: SortOrder | null) => {
    filters.setFilters((prevFilters) => ({
      ...prevFilters,
      sortBy: newSortBy ?? undefined,
      sortOrder: newSortOrder ?? undefined,
    }))
  }

  return uiVersion === 'legacy' ? (
    <LegacyGuests />
  ) : (
    <>
      <TableGroup
        id="visitors-guests"
        columns={columns}
        data={guests}
        error={guestsError}
        getRowKey={(item) => item.id}
        loading={isGuestsFetching}
        mode="regular"
        rowActions={rowActions}
        tableFilters={filters}
        emptyNoFilters={{
          description: t('visitors:dashboard.guests.emptyDescription'),
          title: t('visitors:dashboard.guests.emptyTitle'),
        }}
        title={
          residentId && (
            <Typography as="h2" size="heading-small" color="black800">
              {t('visitors:toolbar.title')}
            </Typography>
          )
        }
        titleActions={
          residentId && (
            <Button size="small" onClick={handleAddGuest}>
              {t('visitors:dashboard.actions.addNew')}
              <Icon icon={PlusIcon} />
            </Button>
          )
        }
        filters={
          !residentId && (
            <Select
              {...residentsOptions.selectProps}
              searchable
              size="small"
              type="multi"
              value={clientId ?? []}
              placeholder={t('visitors:dashboard.filters.residentPlaceholder')}
              dropdownOptions={{ width: ADDITIONAL_FILTERS_DROPDOWN_WIDTH }}
              onChange={handleResidentChange}
              onReset={() => handleResidentChange([])}
            />
          )
        }
        navigation={{
          actions: (
            <Button
              onlyBox
              disabled={isExporting}
              size="small"
              title={t('visitors:dashboard.actions.exportCsv')}
              use="secondary"
              onClick={handleExportCSV}
            >
              <Icon icon={DownloadSimpleIcon} />
            </Button>
          ),
          search: (
            <TextInput
              clearable
              icon={MagnifyingGlassIcon}
              placeholder={t('visitors:dashboard.filters.guestSearchPlaceholder')}
              size="small"
              value={search}
              onChange={handleSearchChange}
            />
          ),
          statusTabs: (
            <StatusTabs options={statusTabOptions} value={status} onChange={handleStatusChange} />
          ),
        }}
        pagination={{
          paginationFilters: filters,
          total,
        }}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
      />
    </>
  )
}
